import { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { isAuthenticated, isEmailVerified, getAuthConfig, logout, getCurrentService } from '../utils/auth';
import AppNav from '../components/AppNav';
import './AppsPage.css';

const API_URL = process.env.REACT_APP_API_URL || window.location.origin;

// Apps that aren't live on this portal (coming_soon / sold_out) are now served
// via the new platform. Their catalog cards link straight to "Get Started" there.
const NEW_PLATFORM_URL = 'https://app.openhosst.com';
const isExternallyServed = (status) => status === 'coming_soon' || status === 'sold_out';

const CATEGORY_COLORS = {
  'Automation':   '#38bdf8',
  'CMS':          '#fb923c',
  'Publishing':   '#a78bfa',
  'AI':           '#40E0D0',
  'DevTools':     '#4ade80',
  'Analytics':    '#f472b6',
  'Productivity': '#fbbf24',
  'Database':     '#60a5fa',
  'Other':        '#6b7280',
};

const BADGE_CONFIG = {
  new:         { label: 'New',         color: '#40E0D0' },
  hot:         { label: 'Hot',         color: '#f97316' },
  coming_soon: { label: 'Coming Soon', color: '#a78bfa' },
  sold_out: { label: 'Sold Out', color: '#f87171' },
  beta:        { label: 'Beta',        color: '#60a5fa' },
  popular:     { label: 'Popular',     color: '#fbbf24' },
};

const SORT_OPTIONS = [
  { value: 'popular', label: 'Most Popular' },
  { value: 'az',      label: 'Name: A – Z' },
  { value: 'price',   label: 'Price: Low to High' },
];

function formatStars(n) {
  if (n == null || n === 0) return null;
  if (n >= 1000) {
    const k = (n / 1000).toFixed(1);
    return k.endsWith('.0') ? k.slice(0, -2) + 'k' : k + 'k';
  }
  return String(n);
}

function AppCard({ svc, onNotify }) {
  const catColor = CATEGORY_COLORS[svc.category] || CATEGORY_COLORS['Other'];
  const stars = formatStars(svc.stars);
  const initial = (svc.name || svc.key).charAt(0).toUpperCase();
  const navigate = useNavigate();
  const authed = isAuthenticated();

  const servedExternally = isExternallyServed(svc.status);
  const handleDeploy = () => {
    if (servedExternally) {
      window.open(NEW_PLATFORM_URL, '_blank', 'noopener,noreferrer');
      return;
    }
    if (authed && isEmailVerified()) {
      navigate(`/${svc.key}`);
    } else if (authed && !isEmailVerified()) {
      navigate('/verify-email');
    } else {
      navigate(`/login?next=/${svc.key}`);
    }
  };

  // Externally-served apps are now available via the new platform, so suppress the
  // Sold Out / Coming Soon badge (keep any other badge like Hot / New).
  let badgeKey = svc.badge || (svc.status === 'coming_soon' ? 'coming_soon' : svc.status === 'sold_out' ? 'sold_out' : null);
  if (servedExternally && (badgeKey === 'coming_soon' || badgeKey === 'sold_out')) badgeKey = null;
  const badge = badgeKey && BADGE_CONFIG[badgeKey];

  return (
    <div className="app-card" onClick={handleDeploy} style={{cursor:'pointer'}}>
      <div className="app-card-top">
        <div className="app-card-icon">
          <span>{initial}</span>
        </div>
        <div className="app-card-badges">
          <span
            className="app-cat-pill"
            style={{ color: catColor, borderColor: `${catColor}40`, background: `${catColor}12` }}
          >
            {svc.category}
          </span>
          {badge && (
            <span className="app-badge-pill"
              style={{ color: badge.color, borderColor: `${badge.color}40`, background: `${badge.color}12` }}>
              {badge.label}
            </span>
          )}
        </div>
      </div>

      <div className="app-card-body">
        <h3 className="app-card-name">{svc.name}</h3>
        {svc.description && <p className="app-card-desc">{svc.description}</p>}
        {svc.tags.length > 0 && (
          <div className="app-card-tags">
            {svc.tags.slice(0, 3).map(t => (
              <span key={t} className="app-tag">#{t}</span>
            ))}
          </div>
        )}
      </div>

      <div className="app-card-footer">
        <div className="app-card-meta">
          {stars && (
            <span className="app-meta-item">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="#fbbf24">
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
              </svg>
              {stars}
            </span>
          )}
          {svc.startingPrice != null && (
            <span className="app-meta-price">from ${svc.startingPrice}/mo</span>
          )}
          {svc.launch_date && svc.status !== 'active' && !servedExternally && (
            <span className="app-card-launch">Est. {svc.launch_date}</span>
          )}
        </div>
        <div>
          {servedExternally
            ? <span className="app-card-btn">Get Started →</span>
            : svc.status === 'presale'
            ? <span className="app-card-btn app-card-btn--presale">Pre-order →</span>
            : <span className="app-card-btn">Deploy →</span>}
        </div>
      </div>
    </div>
  );
}

function AppsPage() {
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch]         = useState('');
  const [activeCategory, setCategory] = useState('All');
  const [sort, setSort]             = useState('popular');

  // Waitlist modal state
  const [waitlistTarget, setWaitlistTarget] = useState(null);
  const [waitlistEmail, setWaitlistEmail] = useState('');
  const [waitlistName, setWaitlistName] = useState('');
  const [waitlistSubmitting, setWaitlistSubmitting] = useState(false);
  const [waitlistDone, setWaitlistDone] = useState(false);
  const [waitlistAlready, setWaitlistAlready] = useState(false);

  useEffect(() => {
    // App Store is browsable without login — no redirect needed
    axios.get(`${API_URL}/api/services`, getAuthConfig())
      .then(res => setServices(res.data.services || []))
      .catch(() => setError('Failed to load apps. Please try again.'))
      .finally(() => setLoading(false));
  }, []);

  const handleLogout = () => { logout(); navigate('/login'); };

  const closeWaitlist = () => {
    setWaitlistTarget(null);
    setWaitlistEmail('');
    setWaitlistName('');
    setWaitlistDone(false);
    setWaitlistAlready(false);
  };

  const submitWaitlist = async (e) => {
    e.preventDefault();
    setWaitlistSubmitting(true);
    try {
      const res = await axios.post(`${API_URL}/api/services/${waitlistTarget.key}/waitlist`, {
        email: waitlistEmail, name: waitlistName
      });
      setWaitlistDone(true);
      setWaitlistAlready(res.data.already || false);
    } catch {
      // ignore, keep modal open
    } finally {
      setWaitlistSubmitting(false);
    }
  };

  // Derive category list from data
  const categories = useMemo(() => {
    const cats = ['All', ...new Set(services.map(s => s.category))].filter(Boolean);
    return cats;
  }, [services]);

  const filtered = useMemo(() => {
    let list = [...services];

    // Category filter
    if (activeCategory !== 'All') list = list.filter(s => s.category === activeCategory);

    // Search
    const q = search.trim().toLowerCase();
    if (q) list = list.filter(s =>
      s.name.toLowerCase().includes(q) ||
      (s.description || '').toLowerCase().includes(q) ||
      s.key.toLowerCase().includes(q) ||
      (s.tags || []).some(t => t.toLowerCase().includes(q))
    );

    // Sort
    if (sort === 'popular') {
      list.sort((a, b) => {
        if (b.stars !== a.stars) return (b.stars || 0) - (a.stars || 0);
        return a.name.localeCompare(b.name);
      });
    } else if (sort === 'az') {
      list.sort((a, b) => a.name.localeCompare(b.name));
    } else if (sort === 'price') {
      list.sort((a, b) => {
        const ap = a.startingPrice ?? Infinity;
        const bp = b.startingPrice ?? Infinity;
        return ap - bp || a.name.localeCompare(b.name);
      });
    }

    return list;
  }, [services, search, activeCategory, sort]);

  // Featured apps (pinned row)
  const featured = useMemo(() => services.filter(s => s.isFeatured), [services]);

  if (loading) {
    return (
      <div className="checkout-container-new">
        <AppNav onLogout={handleLogout} />
        <div className="loading-container"><div className="loading-spinner"></div></div>
        <p className="loading-text">Loading apps...</p>
      </div>
    );
  }

  return (
    <div className="checkout-container-new">
      <AppNav onLogout={handleLogout} />

      <div className="apps-page">

        {/* ── Hero ── */}
        <div className="apps-hero">
          <div className="apps-hero-badge">App Store</div>
          <h1 className="apps-hero-title">Deploy powerful apps effortlessly</h1>
          <p className="apps-hero-sub">
            {services.length} apps available · self-hosted · 7-day free trial on most plans
          </p>

          <div className="apps-search-row">
            <div className="apps-search-wrap">
              <svg className="apps-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              <input
                type="text"
                className="apps-search-input"
                placeholder="Search apps, tags, categories…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                autoComplete="off"
              />
              {search && (
                <button className="apps-search-clear" onClick={() => setSearch('')}>✕</button>
              )}
            </div>
            <select
              className="apps-sort-select"
              value={sort}
              onChange={e => setSort(e.target.value)}
            >
              {SORT_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* ── Category pills ── */}
        <div className="apps-controls">
          <div className="apps-cats">
            {categories.map(cat => (
              <button
                key={cat}
                className={`app-cat-filter${activeCategory === cat ? ' active' : ''}`}
                onClick={() => setCategory(cat)}
              >
                {cat}
                {cat !== 'All' && (
                  <span className="app-cat-count">
                    {services.filter(s => s.category === cat).length}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {error && <div className="alert alert-error" style={{ marginBottom: '20px' }}>{error}</div>}

        {/* ── Featured row ── */}
        {featured.length > 0 && !search && activeCategory === 'All' && (
          <div style={{ marginBottom: '36px' }}>
            <p className="apps-section-label">Featured</p>
            <div className="apps-featured-row">
              {featured.map(svc => <AppCard key={svc.key} svc={svc} onNotify={setWaitlistTarget} />)}
            </div>
          </div>
        )}

        {/* ── Results row ── */}
        <div className="apps-results-row">
          <span className="apps-result-count">
            {filtered.length} result{filtered.length !== 1 ? 's' : ''}
            {activeCategory !== 'All' && ` in ${activeCategory}`}
            {search && ` for "${search}"`}
          </span>
          {(search || activeCategory !== 'All') && (
            <button className="apps-clear-all" onClick={() => { setSearch(''); setCategory('All'); }}>
              Clear filters
            </button>
          )}
        </div>

        {/* ── Grid ── */}
        {filtered.length > 0 ? (
          <div className="apps-grid">
            {filtered.map(svc => <AppCard key={svc.key} svc={svc} onNotify={setWaitlistTarget} />)}
          </div>
        ) : (
          <div className="apps-empty">
            <div className="apps-empty-icon">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
            </div>
            <h3>No apps found</h3>
            <p>Try a different search term or clear your filters.</p>
            <button className="apps-clear-btn" onClick={() => { setSearch(''); setCategory('All'); }}>
              Clear filters
            </button>
          </div>
        )}
      </div>

      {/* ── Waitlist Modal ── */}
      {waitlistTarget && (
        <div className="app-modal-overlay" onClick={closeWaitlist}>
          <div className="app-modal" onClick={e => e.stopPropagation()}>
            {waitlistDone ? (
              <>
                <h3 style={{ margin: '0 0 8px 0' }}>{waitlistAlready ? 'Already registered!' : 'You\'re on the list!'}</h3>
                <p style={{ margin: '0 0 20px 0', color: 'var(--text-secondary, #888)', fontSize: '14px' }}>
                  {waitlistAlready
                    ? `${waitlistEmail} is already registered for ${waitlistTarget.name}.`
                    : `We'll notify you at ${waitlistEmail} when ${waitlistTarget.name} launches.`}
                </p>
                <button className="admin-btn admin-btn--primary" onClick={closeWaitlist}>Close</button>
              </>
            ) : (
              <>
                <h3 style={{ margin: '0 0 4px 0' }}>{waitlistTarget.status === 'sold_out' ? `Get notified when ${waitlistTarget.name} is back in stock` : `Get notified when ${waitlistTarget.name} launches`}</h3>
                {waitlistTarget.launch_date && (
                  <p style={{ margin: '0 0 16px 0', color: 'var(--text-secondary, #888)', fontSize: '13px' }}>
                    Estimated launch: {waitlistTarget.launch_date}
                  </p>
                )}
                <form onSubmit={submitWaitlist}>
                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'block', fontSize: '12px', marginBottom: '4px', color: 'var(--text-secondary, #888)' }}>EMAIL *</label>
                    <input
                      className="admin-input"
                      type="email"
                      required
                      value={waitlistEmail}
                      onChange={e => setWaitlistEmail(e.target.value)}
                      placeholder="you@example.com"
                      autoFocus
                    />
                  </div>
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', fontSize: '12px', marginBottom: '4px', color: 'var(--text-secondary, #888)' }}>NAME (optional)</label>
                    <input
                      className="admin-input"
                      type="text"
                      value={waitlistName}
                      onChange={e => setWaitlistName(e.target.value)}
                      placeholder="Your name"
                    />
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button className="admin-btn admin-btn--primary" type="submit" disabled={waitlistSubmitting}>
                      {waitlistSubmitting ? 'Submitting...' : (waitlistTarget?.status === 'sold_out' ? 'Notify Me When Back' : 'Notify Me')}
                    </button>
                    <button className="admin-btn admin-btn--ghost" type="button" onClick={closeWaitlist}>Cancel</button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default AppsPage;
